import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import TabPanel from '@mui/lab/TabPanel';
import Tab from '@mui/material/Tab';
import classNames from 'classnames';
import Rating from 'components/Rating/Rating';
import ShopRatingDetails from 'components/ShopRating/ShopRatingDetails';
import { createRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ShopService from '../../services/shop.service';
import { ShopType } from '../../types/shop.types';
import { UserType } from '../../types/user.types';
import Loader from '../Loader/Loader';
import { ShopFeedbacks } from '../ShopFeedback/ShopFeedbacks';

interface ShopFeedbackTabContainerProps {
  shop: ShopType;
  isOpen: boolean;
  setOpen: (open?: boolean) => void;
  feedbackReceiver: Partial<UserType> & { shop: Partial<ShopType> };
  refresh?: () => void;
  forceMinHeight?: boolean;
  trackedAutomaticReviewIds: Set<number>;
}

export enum ShopFeedbackTabs {
  ALL = 'all',
  MEMBER = 'member',
  AUTOMATIC = 'automatic',
}

const shopService = new ShopService();

export const ShopFeedbackTab = ({
  shop,
  isOpen,
  setOpen,
  feedbackReceiver,
  refresh,
  forceMinHeight,
  trackedAutomaticReviewIds,
}: ShopFeedbackTabContainerProps) => {
  const { t } = useTranslation();

  const modalContentRef = createRef<HTMLDivElement>();

  const [tabValue, setTabValue] = useState(ShopFeedbackTabs.ALL);
  const [isLoading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState<{
    userFeedbacks: ShopType['userFeedbacks'];
    systemFeedbacks: ShopType['systemFeedbacks'];
    feedbacks: ShopType['feedbacks'];
  } | null>(null);
  const rating = statistics?.feedbacks?.avgRating ? +statistics.feedbacks.avgRating : 0;
  const hasNoReviews = !statistics?.feedbacks?.total;

  const handleChange = (_event: React.SyntheticEvent, newValue: ShopFeedbackTabs) => {
    setTabValue(newValue);
  };

  const fetchShopWithStats = async () => {
    setLoading(true);

    try {
      const fetchedShop = await shopService.getShop(shop?.activeSlug);

      setStatistics({
        userFeedbacks: fetchedShop?.userFeedbacks,
        systemFeedbacks: fetchedShop?.systemFeedbacks,
        feedbacks: fetchedShop?.feedbacks,
      });
    } catch (err) {
      // Do nothing.
    }

    setLoading(false);
  };

  useEffect(() => {
    if (isOpen && shop?.activeSlug) {
      setStatistics(null);
      fetchShopWithStats();
    }
  }, [shop?.activeSlug, isOpen]);

  const handleRefresh = () => {
    fetchShopWithStats();
    refresh?.();
  };

  return (
    <div className={classNames('shop-feedback-tab', { 'shop-feedback-tab--min-height': forceMinHeight })}>
      {isLoading && <Loader />}
      <div className="shop-feedback-totals">
        <div className="rating-box">
          <div className="rating-numeric">{rating.toFixed(1)}</div>
          <Rating feedbacks={statistics?.feedbacks} starSize={16} direction="column" />
        </div>

        <ShopRatingDetails feedbacks={statistics?.feedbacks} />
      </div>

      <TabContext value={tabValue}>
        <TabList onChange={handleChange} className="shop-feedbacks-tabs" textColor="inherit" indicatorColor="primary">
          <Tab label={t('components.feedback.all')} value={ShopFeedbackTabs.ALL} disabled={hasNoReviews} />
          <Tab label={t('components.feedback.from-members')} value={ShopFeedbackTabs.MEMBER} disabled={hasNoReviews} />
          <Tab label={t('components.feedback.automatic')} value={ShopFeedbackTabs.AUTOMATIC} disabled={hasNoReviews} />
        </TabList>

        {!isLoading && statistics && (
          <div ref={modalContentRef} className="shop-feedback-tab-container">
            <TabPanel value={ShopFeedbackTabs.ALL} className="px-0 pb-0">
              <ShopFeedbacks
                isOpen={isOpen}
                setOpen={setOpen}
                modalContentRef={modalContentRef}
                feedbackReceiver={feedbackReceiver}
                filter={ShopFeedbackTabs.ALL}
                refresh={handleRefresh}
                trackedAutomaticReviewIds={trackedAutomaticReviewIds}
              />
            </TabPanel>
            <TabPanel value={ShopFeedbackTabs.MEMBER} className="px-0 pb-0">
              <ShopFeedbacks
                isOpen={isOpen}
                setOpen={setOpen}
                modalContentRef={modalContentRef}
                feedbackReceiver={feedbackReceiver}
                filter={ShopFeedbackTabs.MEMBER}
                refresh={handleRefresh}
                trackedAutomaticReviewIds={trackedAutomaticReviewIds}
              />
            </TabPanel>
            <TabPanel value={ShopFeedbackTabs.AUTOMATIC} className="px-0 pb-0">
              <ShopFeedbacks
                isOpen={isOpen}
                setOpen={setOpen}
                modalContentRef={modalContentRef}
                feedbackReceiver={feedbackReceiver}
                filter={ShopFeedbackTabs.AUTOMATIC}
                refresh={handleRefresh}
                trackedAutomaticReviewIds={trackedAutomaticReviewIds}
              />
            </TabPanel>
          </div>
        )}
      </TabContext>
    </div>
  );
};
